import { StagingPriority } from 'data/pps/mockData';
import { makeAutoObservable, runInAction } from 'mobx';
import { getStagingPriorityListService } from 'services/StagingPriorityListServices/getStagingPriorityListService';
import { handleApiError } from 'utils/handleApiError';
import { toast } from 'react-toastify';

export class StagingPriorityStore {
  isLoading: boolean = false;
  isPolling: boolean = false;
  stagingPriorityData: StagingPriority[] = [];
  error: string | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchStagingPriorityData = async () => {
    this.isLoading = true;
    try {
      const response = await getStagingPriorityListService.getStagingPriorityList();

      runInAction(() => {
        if (response.success) {
          this.stagingPriorityData = response.staging_priority_list;
        } else {
          this.stagingPriorityData = [];
          this.error = response.message || 'Failed to fetch staging priority data';
        }
      });
    } catch (error: unknown) {
      this.stagingPriorityData = [];
      this.error = error instanceof Error ? error.message : 'Unknown error';
      handleApiError(error, 'Error occurred while fetching staging priority data');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollStagingPriorityData = async () => {
    this.isPolling = true;
    try {
      const response = await getStagingPriorityListService.getStagingPriorityList();

      runInAction(() => {
        if (response.success) {
          // Update data only if it has changed
          if (
            JSON.stringify(this.stagingPriorityData) !==
            JSON.stringify(response.staging_priority_list)
          ) {
            this.stagingPriorityData = response.staging_priority_list;
          }
        }
      });
    } catch (error: unknown) {
      // Don't show error for polling failures to avoid spam
      if(this.environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async () => {
    await this.fetchStagingPriorityData();
  };
}
